import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Search, Check, X, AlertCircle } from "lucide-react";

export default function VendorOutreachPanel() {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState("");
  const [sending, setSending] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchTrucks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (cityFilter.trim()) params.city = cityFilter.trim();
      const res = await axios.get(`${API}/admin/unclaimed-trucks`, { params });
      setTrucks(res.data.trucks || []);
      setLastRefreshedAt(Date.now());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [cityFilter]);

  useEffect(() => { fetchTrucks(); }, [fetchTrucks]);

  const handleSend = async (businessId) => {
    setSending(businessId);
    try {
      const res = await axios.post(`${API}/admin/send-outreach`, { business_id: businessId });
      toast.success(`Outreach ${res.data.status} for ${res.data.truck_name}`);
      fetchTrucks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSending(null); }
  };

  const handleBulkSend = async () => {
    if (!cityFilter.trim()) {
      toast.error("Enter a city to bulk send");
      return;
    }
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-outreach`, { city: cityFilter.trim() });
      toast.success(`Bulk outreach: ${res.data.sent} sent in ${res.data.city}`);
      fetchTrucks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk send failed");
    } finally { setBulkSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="vendor-outreach-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchTrucks} lastRefreshedAt={lastRefreshedAt} testId="vendor-outreach-refresh" /></div>
      {/* Search + Bulk */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
            placeholder="Filter by city..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-amber-400"
            data-testid="outreach-city-filter"
          />
        </div>
        <button
          onClick={handleBulkSend}
          disabled={bulkSending || !cityFilter.trim()}
          className="px-3 py-2 bg-amber-500 text-white text-xs font-bold rounded-lg flex items-center gap-1 disabled:opacity-40"
          data-testid="bulk-send-btn"
        >
          {bulkSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Bulk Send
        </button>
      </div>

      <p className="text-[10px] text-slate-400">
        {trucks.length} unclaimed truck{trucks.length !== 1 ? "s" : ""} found. Rate limit: 50 emails/hour.
      </p>

      {/* Truck list */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
        </div>
      ) : trucks.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-xs">
          No unclaimed trucks found{cityFilter ? ` in "${cityFilter}"` : ""}
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {trucks.map((truck, i) => (
            <div
              key={truck.business_id}
              className="flex items-center gap-3 p-3 bg-white border border-slate-100 rounded-xl"
              data-testid={`outreach-truck-${i}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{truck.name}</p>
                <p className="text-[10px] text-slate-400">
                  {truck.city}, {truck.state} — Added {truck.created_at ? new Date(truck.created_at).toLocaleDateString() : "N/A"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {truck.has_birthday_offer && (
                    <span className="text-[8px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold">Birthday Offer</span>
                  )}
                  <span className="text-[8px] text-slate-300">
                    Profile: {truck.profile_completeness || 0}%
                  </span>
                  {truck.outreach_sent && (
                    <span className="text-[8px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5">
                      <Check className="w-2 h-2" /> Sent {truck.outreach_sent_at ? new Date(truck.outreach_sent_at).toLocaleDateString() : ""}
                    </span>
                  )}
                </div>
              </div>

              {truck.has_contact_email ? (
                <button
                  onClick={() => handleSend(truck.business_id)}
                  disabled={sending === truck.business_id || truck.outreach_sent}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 shrink-0 ${
                    truck.outreach_sent
                      ? "bg-slate-100 text-slate-400"
                      : "bg-emerald-500 text-white hover:bg-emerald-600"
                  } disabled:opacity-50`}
                  data-testid={`send-outreach-${i}`}
                >
                  {sending === truck.business_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : truck.outreach_sent ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Mail className="w-3 h-3" />
                  )}
                  {truck.outreach_sent ? "Sent" : "Send"}
                </button>
              ) : (
                <span className="text-[9px] text-red-400 flex items-center gap-0.5 shrink-0">
                  <AlertCircle className="w-3 h-3" /> No email
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
